/**
 * Canonical inbound approval contract — the wire shape of the voll-product Kafka
 * event `policy.approval.requested`.
 *
 * Producer: the foreign `policy-business-api` service, emitted from 4 identical
 * sites (first/second/manager/parallel level approval streams) as
 * `{ voucher, userApprovals }`. The `voucher` object is the full
 * `policy.rules.validate.requested` payload passed through; `userApprovals` are the
 * already-resolved approvers (escalation levels + delegates merged by the product).
 *
 * Consumers: the Phase 2 approval integrator (decode + enrich + notify) and the
 * Phase 3 `approvals` domain agent. Defined once here so both share ONE shape.
 *
 * This is a cross-service WIRE contract, NOT a persisted Mongo document, so it
 * carries no `_schemaVersion`.
 *
 * Dates stay RAW strings — never `z.date()` / `z.coerce.date()`. The producer emits
 * TRUE-UTC values; coercing to Date risks the voucher-date-fidelity wall-clock
 * shift. Carry the string through untouched; parse only at the display boundary.
 *
 * Foreign field names (`userApprovals`, `externalCode`, `delegateBy`) are kept
 * verbatim to match the bus — they are NOT renamed to our persona vocabulary.
 *
 * Only the fields the integrator/agent actually consume are pinned. Unknown extra
 * producer fields on `voucher` (e.g. `policies`, `journey`, `isEvent`) are tolerated:
 * a plain `z.object` STRIPS unknown keys in Zod v4, so a producer-side field add
 * never breaks decode.
 */
import { z } from 'zod';

const OptionalWireStringSchema = z
  .string()
  .nullish()
  .transform((value) => value ?? undefined);

/** One resolved approver. `delegateBy` is present when a substitute resolved. */
export const ApproverSchema = z.object({
  id: z.number(),
  // The producer has emitted nulls for these denormalized identity fields on a
  // slice of approvals. Keep decode tolerant; consumers decide whether a given
  // operation can proceed without each field.
  name: OptionalWireStringSchema,
  email: OptionalWireStringSchema,
  externalCode: OptionalWireStringSchema,
  delegateBy: z
    .object({
      id: z.number().optional(),
      name: OptionalWireStringSchema,
      email: OptionalWireStringSchema,
    })
    .nullish()
    .transform((value) => value ?? undefined),
});
export type Approver = z.infer<typeof ApproverSchema>;

/**
 * The voucher subset the approval flow needs. Mandatory fields fail loud when
 * absent (the integrator cannot notify without them); `company`/`costCenter` are
 * optional and only carry the fields downstream consumes.
 */
export const ApprovalVoucherSchema = z.object({
  id: z.string(),
  voucherCode: z.string(),
  modal: z.string(),
  /**
   * Purchase origin (`voucher.channel`): the producer sends the whole voucher; null/absent =
   * unknown. Any non-string or blank value reads as unknown too, never dropping the approval.
   */
  channel: z.preprocess(
    (value) => (typeof value === 'string' && value.trim() !== '' ? value : undefined),
    z.string().min(1).optional(),
  ),
  traveler: z.object({
    id: z.number(),
    name: z.string(),
    email: OptionalWireStringSchema,
    externalCode: OptionalWireStringSchema,
  }),
  client: z.object({
    id: z.number(),
  }),
  company: z
    .object({
      id: z.number(),
      name: z.string(),
      externalCode: OptionalWireStringSchema,
    })
    .optional(),
  costCenter: z
    .object({
      id: z.number(),
      name: z.string(),
      externalCode: OptionalWireStringSchema,
    })
    .optional(),
});
export type ApprovalVoucher = z.infer<typeof ApprovalVoucherSchema>;

export const ApprovalRequestedSchema = z.object({
  voucher: ApprovalVoucherSchema,
  userApprovals: z.array(ApproverSchema),
});
export type ApprovalRequested = z.infer<typeof ApprovalRequestedSchema>;
